import React, { useState, useRef, useEffect, useContext, useMemo } from "react"
import { useHistory } from "react-router-dom"
import {
  Tabs,
  Tab,
  Popover,
  IconButton,
  Badge,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  Avatar,
  ListItemAvatar,
} from "@material-ui/core"
import useMediaQuery from "@material-ui/core/useMediaQuery"
import { useTheme } from "@material-ui/core/styles"
import { Bell } from "lucide-react"
import { SocketContext } from "../../context/Socket/SocketContext"
import { AuthContext } from "../../context/Auth/AuthContext"
import useSound from "use-sound"
import useTickets from "../../hooks/useTickets"
import useUsuariosPermitidosStore from "../../stores/usuariosPermitidosStore"
import alertSound from "../../assets/sound.mp3"
import TicketListItemCustom from "../TicketListItemCustom"
import toastError from "../../errors/toastError"
import moment from "moment"
import { format } from "date-fns"
import api from "../../services/api"
import { isArray } from "lodash"
import useStyles from "./styles"
import getUserProfileValidate from "../../helpers/getUserProfileValidate"
import {
  filasUsuarioPermitidasCalcular,
  notificacaoConversaUsuarioDeveAlertar,
  ticketTemMensagensNaoLidas,
} from "../../helpers/ticketsStoreHelpers"
import { useLocation } from "react-router-dom/cjs/react-router-dom.min"
import { canais } from "../../utils/canais"

// eslint-disable-next-line
const reducer = (state, action) => {
  if (action.type === "LOAD_ANNOUNCEMENTS") {
    const announcements = action.payload
    const newAnnouncements = []

    if (isArray(announcements)) {
      announcements.forEach((announcement) => {
        const announcementIndex = state.findIndex((u) => u.id === announcement.id)
        if (announcementIndex !== -1) {
          state[announcementIndex] = announcement
        } else {
          newAnnouncements.push(announcement)
        }
      })
    }

    return [...state, ...newAnnouncements]
  }

  if (action.type === "UPDATE_ANNOUNCEMENTS") {
    const announcement = action.payload
    const announcementIndex = state.findIndex((u) => u.id === announcement.id)

    if (announcementIndex !== -1) {
      state[announcementIndex] = announcement
      return [...state]
    } else {
      return [announcement, ...state]
    }
  }

  if (action.type === "DELETE_ANNOUNCEMENT") {
    const announcementId = action.payload

    const announcementIndex = state.findIndex((u) => u.id === announcementId)
    if (announcementIndex !== -1) {
      state.splice(announcementIndex, 1)
    }
    return [...state]
  }

  if (action.type === "RESET") {
    return []
  }
}

const AnnouncementDialog = ({ announcement, open, handleClose }) => {
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{announcement.title}</DialogTitle>
      <DialogContent>
        {announcement.arquivoUrl && (
          <div
            style={{
              border: "1px solid #f1f1f1",
              margin: "0 auto 20px",
              textAlign: "center",
              width: "400px",
              height: 300,
              backgroundImage: `url(${announcement.arquivoUrl})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "contain",
              backgroundPosition: "center",
            }}
          ></div>
        )}
        <DialogContentText id="alert-dialog-description">{announcement.text}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" autoFocus>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const UnifiedPopover = () => {
  const classes = useStyles()
  const theme = useTheme()
  const lMobile = useMediaQuery(theme.breakpoints.down("xs"))
  const { user } = useContext(AuthContext)
  const socketManager = useContext(SocketContext)
  const [anchorEl, setAnchorEl] = useState(null)
  const [tabIndex, setTabIndex] = useState(0)
  const [notifications, setNotifications] = useState([])
  const [announcements, setAnnouncements] = useState([])
  const [playAlert] = useSound(alertSound)
  const soundAlertRef = useRef()
  const lPopoverAberto = Boolean(anchorEl)
  const { tickets } = useTickets({ apenasNaoLidas: "true", lHabilitado: lPopoverAberto })
  const ticketIdRef = useRef()
  const history = useHistory()
  const [announcement, setAnnouncement] = useState({})
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false)
  const oFilasDoUserLogado = useMemo(() => filasUsuarioPermitidasCalcular(user), [user])
  const location = useLocation()
  const isUserPermitted = useUsuariosPermitidosStore((state) => state.isUserPermitted)

  useEffect(() => {
    soundAlertRef.current = playAlert
    if (!("Notification" in window)) {
      console.log("This browser doesn't support notifications")
    } else {
      Notification.requestPermission()
    }
  }, [playAlert])

  useEffect(() => {
    if (!lPopoverAberto) return

    let lAtivo = true

    const buscarInformativos = async () => {
      try {
        const { data: oResposta } = await api.get("/announcements/", {
          params: { pageNumber: 1 },
        })
        if (!lAtivo) return
        const mInformativos = oResposta.records?.filter((oInformativo) => oInformativo.status) || []
        setAnnouncements(mInformativos)
      } catch (oErro) {
        toastError(oErro)
      }
    }

    buscarInformativos()

    return () => {
      lAtivo = false
    }
  }, [lPopoverAberto])

  // Memoriza o processamento de notificações para evitar recálculos desnecessários
  const mTicketsComMensagensNaoLidas = useMemo(() => {
    return tickets.filter((oTicket) => {
      if (!ticketTemMensagensNaoLidas(oTicket, user)) return false

      return notificacaoConversaUsuarioDeveAlertar({
        ticket: oTicket,
        user,
        oFilasDoUserLogado,
        isUserPermitted,
      })
    })
  }, [tickets, user, oFilasDoUserLogado, isUserPermitted])

  useEffect(() => {
    setNotifications(mTicketsComMensagensNaoLidas)
  }, [mTicketsComMensagensNaoLidas])

  useEffect(() => {
    ticketIdRef.current = +history.location.pathname.split("/")[2]
  }, [history.location.pathname])

  const [isLigacao, setIsLigacao] = useState(false)

  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId)
    socket.on("ready", () => socket.emit("joinNotification"))

    const handleAppMessage = (data) => {
      if (data.action === "create") {
        const isCallMessage =
          data.message.body ===
            "*Mensagem Automática:*\n\nAs chamadas de voz e vídeo estão desabilitadas para esse WhatsApp, favor enviar uma mensagem de texto. Obrigado" &&
          data.message.fromMe

        setIsLigacao(isCallMessage)

        if (isCallMessage || !data.message.fromMe) {
          const oTicket = data.message.ticket
          const nUsuarioIdMensagem = data.message.usuarioId
          const lEhConversaInterna = oTicket.canalTipoId === canais.interno

          // Conversa interna: não alerta o próprio autor nem quem não participa da conversa.
          if (
            lEhConversaInterna &&
            (nUsuarioIdMensagem === user.id || !oTicket.conversaParticipantes.some((p) => p.usuarioId === user.id))
          ) {
            return
          }

          // Supressões de UX (usuário já está vendo a conversa) — válidas só para conversas externas.
          const lSuprimirPorUx =
            !lEhConversaInterna &&
            ((oTicket.id === ticketIdRef.current && document.visibilityState === "visible") ||
              location.pathname.includes(data.message.ticket.uuid) ||
              !getUserProfileValidate(user, data))

          const lDeveAlertar =
            !lSuprimirPorUx &&
            notificacaoConversaUsuarioDeveAlertar({
              ticket: oTicket,
              user,
              oFilasDoUserLogado,
              isUserPermitted,
            })

          if (lDeveAlertar) {
            setNotifications((prevState) => {
              const ticketIndex = prevState.findIndex((t) => t.id === oTicket.id)
              if (ticketIndex !== -1) {
                prevState[ticketIndex] = oTicket
                return [...prevState]
              }
              return [oTicket, ...prevState]
            })

            handleNotification(data)
          }
        }
      }
    }

    socket.on(`company-${user.companyId}-appMessage`, handleAppMessage)

    return () => {
      socket.off(`company-${user.companyId}-appMessage`, handleAppMessage)
    }
  }, [user, socketManager, oFilasDoUserLogado, location.pathname, history.location.pathname, isUserPermitted])

  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId)

    const handleReady = () => socket.emit("joinNotification")
    const handleCompanyTicket = (data) => {
      if (data.action === "updateUnread" || data.action === "delete" || isLigacao) {
        setNotifications((prevState) => {
          const ticketIndex = prevState.findIndex((t) => t.id === data.ticketId)

          if (ticketIndex !== -1) {
            // Se for um delete ou chamada de voz, remove sempre
            if (data.action === "delete" || isLigacao) {
              prevState.splice(ticketIndex, 1)
              return [...prevState]
            }

            // Se for updateUnread, verifica ACL e se o ticket ainda tem mensagens não lidas
            if (data.action === "updateUnread") {
              const oTicket = data.ticket || prevState[ticketIndex]

              const lTemAcesso = notificacaoConversaUsuarioDeveAlertar({
                ticket: oTicket,
                user,
                oFilasDoUserLogado,
                isUserPermitted,
              })

              if (!lTemAcesso || !ticketTemMensagensNaoLidas(oTicket, user)) {
                prevState.splice(ticketIndex, 1)
                return [...prevState]
              }

              prevState[ticketIndex] = oTicket
              return [...prevState]
            }
          }

          return prevState
        })
      }
    }

    const handleCompanyAnnouncement = (data) => {
      if (data.action === "create") {
        setAnnouncements((prevState) => {
          const alreadyExists = prevState.some((a) => a.id === data.record.id)
          if (!alreadyExists && data.record.status) {
            return [data.record, ...prevState]
          }
          return prevState
        })
      } else if (data.action === "update") {
        setAnnouncements((prevState) => {
          const nInformativoIndex = prevState.findIndex((a) => a.id === data.record.id)

          /** Se já está na lista, remove para inserir a versão atualizada. */
          if (nInformativoIndex !== -1) {
            prevState.splice(nInformativoIndex, 1)
          }

          /** Insere o informativo na lista, se estiver ativo. */
          if (data.record.status) {
            return [data.record, ...prevState]
          }

          return prevState
        })
      } else if (data.action === "delete") {
        setAnnouncements((prevState) => {
          const ticketIndex = prevState.findIndex((a) => a.id === Number.parseInt(data.id))
          if (ticketIndex !== -1) {
            prevState.splice(ticketIndex, 1)
            return [...prevState]
          }
          return prevState
        })
      }
    }

    socket.on("ready", handleReady)
    socket.on(`company-${user.companyId}-ticket`, handleCompanyTicket)
    socket.on(`company-announcement`, handleCompanyAnnouncement)

    return () => {
      socket.off("ready", handleReady)
      socket.off(`company-${user.companyId}-ticket`, handleCompanyTicket)
      socket.off(`company-announcement`, handleCompanyAnnouncement)
    }
    // eslint-disable-next-line
  }, [user, socketManager, isLigacao])

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleNotification = async (data) => {
    const oMessage = data?.message || data?.ticket?.messages?.[0]
    const oTicket = oMessage?.ticket ?? data?.ticket
    const oContato = oTicket?.contact

    if (!oTicket || !oContato) return

    const cTitle = `Mensagem de ${oContato.name}`
    const cTag = oTicket.id ? oTicket.id.toString() : Date.now().toString()

    const oOptions = {
      body: `${oMessage.body} - ${format(new Date(), "HH:mm")}`,
      icon: oContato.profilePicUrl,
      tag: cTag,
      renotify: true,
    }

    try {
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        const oRegistration = await navigator.serviceWorker.ready
        await oRegistration.showNotification(cTitle, oOptions)
      } else {
        new Notification(cTitle, oOptions)
      }
    } catch (oErr) {
      console.error("Erro ao exibir notificação:", oErr)
    }

    soundAlertRef.current()
  }

  const handleNotificationClick = (data) => {
    const ticketInfo = api.get(`/tickets/${data}`)

    if (ticketInfo.uuid) {
      history.push(`/conversas/${ticketInfo.uuid}`)
    }

    setAnchorEl(null)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setTabIndex(0)
  }

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue)
  }

  const handleShowAnnouncementDialog = (record) => {
    setAnnouncement(record)
    setShowAnnouncementDialog(true)
    setTabIndex(0)
    setAnchorEl(null)
  }

  const id = lPopoverAberto ? "unified-popover" : undefined

  const hasNotifications = notifications.length > 0 || announcements.length > 0

  return (
    <div>
      <AnnouncementDialog
        announcement={announcement}
        open={showAnnouncementDialog}
        handleClose={() => setShowAnnouncementDialog(false)}
      />
      <IconButton aria-label="Abrir notificações" onClick={handleClick} className={classes.elementBar}>
        <Badge color="secondary" variant={hasNotifications ? "dot" : "standard"}>
          <Bell className={classes.iconBar} size={"24px"} strokeWidth={1.6} />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={lPopoverAberto}
        anchorEl={anchorEl}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: lMobile ? "right" : "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        classes={{ paper: classes.popoverPaper }}
      >
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered
          variant={lMobile ? "fullWidth" : "standard"}
          classes={{ indicator: classes.tabIndicator }}
        >
          <Tab className={classes.tabs} label="Notificações" />
          <Tab className={classes.tabs} label="Informativos" />
          {/*<Tab label="Chats" />*/}
        </Tabs>
        <Paper className={classes.mainPaper}>
          {tabIndex === 0 && (
            <List className={classes.tabContainer}>
              {notifications.length === 0 ? (
                <ListItem>
                  <ListItemText>Nenhuma notificação.</ListItemText>
                </ListItem>
              ) : (
                notifications.map((ticket) => (
                  <TicketListItemCustom
                    key={ticket.id}
                    ticket={ticket}
                    handleClick={handleNotificationClick}
                    showOptionsMenu={false}
                  />
                ))
              )}
            </List>
          )}
          {tabIndex === 1 && (
            <List className={classes.tabContainer}>
              {announcements.length === 0 ? (
                <ListItem>
                  <ListItemText>Nenhum informativo.</ListItemText>
                </ListItem>
              ) : (
                announcements.map((item, key) => (
                  <ListItem
                    key={key}
                    style={{
                      border: "1px solid #eee",
                      cursor: "pointer",
                    }}
                    onClick={() => handleShowAnnouncementDialog(item)}
                  >
                    {item.arquivoUrl && (
                      <ListItemAvatar>
                        <Avatar alt={item.arquivoNome} src={item.arquivoUrl} />
                      </ListItemAvatar>
                    )}
                    <ListItemText
                      primary={item.title}
                      secondary={
                        <>
                          <Typography component="span" style={{ fontSize: 12 }}>
                            {moment(item.createdAt).format("DD/MM/YYYY")}
                          </Typography>
                          <span style={{ marginTop: 5, display: "block" }}></span>
                          <Typography component="span" variant="body2">
                            {item.text}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))
              )}
            </List>
          )}
        </Paper>
      </Popover>
    </div>
  )
}

export default UnifiedPopover
